import React, { useMemo, useState } from 'react';
import { Container, Row, Col, Card, CardBody, FormGroup, Label, Input, Spinner } from 'reactstrap';
import { Helmet } from 'react-helmet';
import Breadcrumbs from 'components/Common/Breadcrumb';
import RolePermissionsEditor from 'components/Tenant/Permissions/RolePermissionsEditor';
import { useRolesListQuery } from 'http-client/hooks/useRoleHooks';

const PermissionsPage: React.FC = () => {
  const { data: roles, isLoading } = useRolesListQuery();
  const [selectedRoleId, setSelectedRoleId] = useState<string>('');

  const selectedRole = useMemo(
    () => roles?.find((r) => r.id === selectedRoleId),
    [roles, selectedRoleId],
  );

  const editableRoles = useMemo(() => roles ?? [], [roles]);

  React.useEffect(() => {
    if (!selectedRoleId && editableRoles.length > 0) {
      setSelectedRoleId(editableRoles[0]?.id ?? '');
    }
  }, [editableRoles, selectedRoleId]);

  return (
    <div className="page-content">
      <Helmet>
        <title>Permissions</title>
      </Helmet>
      <Container fluid>
        <Breadcrumbs title="Permissions" breadcrumbItem="Role & Permission Settings" />
        <Row>
          <Col lg="12">
            <Card className="shadow-sm mb-4">
              <CardBody>
                <Row className="align-items-end">
                  <Col md={6} lg={4}>
                    <FormGroup>
                      <Label for="roleSelect" className="fw-bold">
                        Select Role
                      </Label>
                      {isLoading ? (
                        <Spinner size="sm" color="primary" />
                      ) : (
                        <Input
                          id="roleSelect"
                          type="select"
                          value={selectedRoleId}
                          onChange={(e) => setSelectedRoleId(e.target.value)}
                        >
                          <option value="">Choose a role...</option>
                          {editableRoles.map((role) => (
                            <option key={role.id} value={role.id}>
                              {role.name}
                              {role.isSystemRole ? ' (system)' : ''}
                            </option>
                          ))}
                        </Input>
                      )}
                    </FormGroup>
                  </Col>
                  {selectedRole && (
                    <Col md={6} lg={8}>
                      <p className="text-muted mb-0">
                        {selectedRole.description ||
                          'Configure module permissions for this role. Selecting a category toggles all permissions within it.'}
                      </p>
                    </Col>
                  )}
                </Row>
              </CardBody>
            </Card>

            {selectedRoleId && selectedRole ? (
              <RolePermissionsEditor
                roleId={selectedRoleId}
                roleName={selectedRole.name}
                canEdit={!selectedRole.isSystemRole}
              />
            ) : (
              !isLoading && (
                <Card>
                  <CardBody className="text-center text-muted py-5">
                    Select a role to manage its permissions.
                  </CardBody>
                </Card>
              )
            )}
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default PermissionsPage;
